'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { registrationService } from '@/lib/services/registrationService';

interface StorySubmissionFormProps {
  onClose: () => void;
}

const StorySubmissionForm = ({ onClose }: StorySubmissionFormProps) => {
  const [formData, setFormData] = useState({
    displayName: '',
    location: '',
    storyTitle: '',
    transformation: '',
    quote: '',
    email: '',
    useRealName: false,
    sharePublicly: true,
  });
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await registrationService.submitStory({
        displayName: formData.displayName,
        location: formData.location,
        storyTitle: formData.storyTitle,
        transformation: formData.transformation,
        quote: formData.quote,
        email: formData.email,
        useRealName: formData.useRealName,
        sharePublicly: formData.sharePublicly,
        photoUrl: photoPreview || undefined,
      });

      if (result.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Failed to submit story. Please try again.');
      }
    } catch (err: any) {
      setError('An unexpected error occurred. Please try again later.');
      console.error('Story submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={48} className="text-success" />
          </div>
          <h3 className="text-3xl font-headline font-bold text-foreground mb-3">
            Thank You!
          </h3>
          <p className="text-lg font-body text-muted-foreground">
            Your transformation story has been submitted. Our team will review it and reach out via email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-2xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b border-border">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground">
            Share Your Transformation Story
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-full transition-colors duration-200"
            aria-label="Close form"
          >
            <Icon name="XMarkIcon" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          {/* Privacy Notice */}
          <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-lg">
            <div className="flex items-start space-x-4">
              <Icon name="ShieldCheckIcon" size={28} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-body font-semibold text-foreground text-lg mb-2">
                  Your Privacy is Protected
                </h3>
                <p className="text-base font-body text-muted-foreground">
                  You control what information is shared. Use a pseudonym if preferred, and we'll never publish without your consent.
                </p>
              </div>
            </div>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-base font-body font-semibold text-foreground mb-3">
              Your Photo <span className="text-destructive">*</span>
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary transition-colors duration-200">
              {photoPreview ? (
                <div className="space-y-4">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-32 h-32 object-cover rounded-full mx-auto"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreview(null);
                      setPhotoFile(null);
                    }}
                    className="text-base font-body text-destructive hover:underline"
                  >
                    Remove Photo
                  </button>
                </div>
              ) : (
                <div>
                  <Icon name="PhotoIcon" size={56} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-base font-body text-muted-foreground mb-4">
                    Upload a photo (optional: can be symbolic or representative)
                  </p>
                  <label className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg font-body text-base font-medium cursor-pointer hover:bg-primary/90 transition-colors duration-200">
                    Choose Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                      required
                    />
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="displayName" className="block text-base font-body font-semibold text-foreground mb-3">
              Display Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              placeholder="How should we identify you? (e.g., Sarah M., Anonymous, etc.)"
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
            <div className="mt-3 flex items-center space-x-3">
              <input
                type="checkbox"
                id="useRealName"
                name="useRealName"
                checked={formData.useRealName}
                onChange={handleInputChange}
                className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="useRealName" className="text-base font-body text-muted-foreground">
                This is my real name (optional)
              </label>
            </div>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-base font-body font-semibold text-foreground mb-3">
              Location <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="City, Country (can be general, e.g., 'East Africa')"
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Story Title */}
          <div>
            <label htmlFor="storyTitle" className="block text-base font-body font-semibold text-foreground mb-3">
              Story Title <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="storyTitle"
              name="storyTitle"
              value={formData.storyTitle}
              onChange={handleInputChange}
              placeholder="Give your transformation a title"
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Your Quote */}
          <div>
            <label htmlFor="quote" className="block text-base font-body font-semibold text-foreground mb-3">
              Your Story in Your Words <span className="text-destructive">*</span>
            </label>
            <textarea
              id="quote"
              name="quote"
              value={formData.quote}
              onChange={handleInputChange}
              placeholder="Share your journey of transformation... What happened? How did choosing 'no enemy' change your life?"
              rows={6}
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-all"
              required
            />
          </div>

          {/* Transformation Summary */}
          <div>
            <label htmlFor="transformation" className="block text-base font-body font-semibold text-foreground mb-3">
              Transformation Summary <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              id="transformation"
              name="transformation"
              value={formData.transformation}
              onChange={handleInputChange}
              placeholder="Brief summary (e.g., 'Forgave family member after 10 years')"
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-base font-body font-semibold text-foreground mb-3">
              Email Address <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              className="w-full px-6 py-4 border border-border rounded-lg font-body text-base focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              required
            />
            <p className="text-sm font-body text-muted-foreground mt-2">
              We'll use this to contact you about your submission. Never shared publicly.
            </p>
          </div>

          {/* Sharing Consent */}
          <div className="bg-muted/30 p-6 rounded-lg">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="sharePublicly"
                name="sharePublicly"
                checked={formData.sharePublicly}
                onChange={handleInputChange}
                className="w-5 h-5 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="sharePublicly" className="text-base font-body text-foreground">
                I consent to sharing my story publicly after review
              </label>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-destructive/10 border-l-4 border-destructive p-6 rounded-lg">
              <p className="text-base font-body text-destructive">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-8 py-4 border border-border rounded-lg font-body text-base text-muted-foreground hover:bg-muted transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-body text-base font-bold hover:bg-primary/90 transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Story'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StorySubmissionForm;